import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import ProfileTab from './components/ProfileTab';
import SubscriptionTab from './components/SubscriptionTab';
import NotificationsTab from './components/NotificationsTab';
import PreferencesTab from './components/PreferencesTab';
import PaymentTab from './components/PaymentTab';
import SecurityTab from './components/SecurityTab';

const AccountSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: 'User',
      description: 'Personal information and contact details'
    },
    {
      id: 'subscription',
      label: 'Subscription',
      icon: 'Crown',
      description: 'Plan details and billing information'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'Bell',
      description: 'Email and push notification preferences'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      description: 'Export defaults and interface settings'
    },
    {
      id: 'payment',
      label: 'Payment',
      icon: 'CreditCard',
      description: 'Payment methods and billing address'
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Password, 2FA, and login activity'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />;
      case 'subscription':
        return <SubscriptionTab />;
      case 'notifications':
        return <NotificationsTab />;
      case 'preferences':
        return <PreferencesTab />;
      case 'payment':
        return <PaymentTab />;
      case 'security':
        return <SecurityTab />;
      default:
        return <ProfileTab />;
    }
  };

  const currentTab = tabs?.find(tab => tab?.id === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="user" userName="John Doe" />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Settings" size={20} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
                <p className="text-muted-foreground">Manage your account preferences and security settings</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Desktop Sidebar */}
            <div className="hidden lg:block">
              <div className="bg-card border border-border rounded-lg p-4 sticky top-24">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-left transition-micro ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{tab?.label}</div>
                        <div className={`text-xs truncate ${
                          activeTab === tab?.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                        }`}>
                          {tab?.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Mobile Tab Selector */}
            <div className="lg:hidden col-span-full">
              <div className="bg-card border border-border rounded-lg p-4 mb-6">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-micro"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={currentTab?.icon} size={18} />
                    <div className="text-left">
                      <div className="font-medium text-foreground">{currentTab?.label}</div>
                      <div className="text-xs text-muted-foreground">{currentTab?.description}</div>
                    </div>
                  </div>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className={`transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`} 
                  />
                </button>

                {isMobileMenuOpen && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1">
                    {tabs?.filter(tab => tab?.id !== activeTab)?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => {
                          setActiveTab(tab?.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left hover:bg-muted transition-micro"
                      >
                        <Icon name={tab?.icon} size={16} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-foreground">{tab?.label}</div>
                          <div className="text-xs text-muted-foreground truncate">{tab?.description}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-6">
                {/* Tab Content Header - Mobile Only */}
                <div className="lg:hidden bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={currentTab?.icon} size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">{currentTab?.label}</h2>
                      <p className="text-sm text-muted-foreground">{currentTab?.description}</p>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;